import type { InternalTicket, Invoice, InvoiceV2, ServiceLocationTransport } from '@alliance-disposal/transport-types';
import type { BillingPeriodsApiOperationsV1BillingPeriodsGetRequest, TasksApiOperationsV1TasksGetRequest } from '@wayste/operations-client';
import type { UseTaskGroupsParams } from './use-task-groups';

export const EQUIPMENT_TYPE_QUERY_KEYS = {
    equipmentTypes: 'equipmentTypes',
    equipmentType: (id: string) => [EQUIPMENT_TYPE_QUERY_KEYS.equipmentTypes, 'equipmentType', id],
} as const;

export const SERVICE_LINE_QUERY_KEYS = {
    serviceLines: 'serviceLines',
    serviceLine: (id: string) => [SERVICE_LINE_QUERY_KEYS.serviceLines, 'serviceLine', id],
} as const;

export const SUBSCRIPTION_QUERY_KEYS = {
    subscriptions: 'subscriptions',
    subscription: (id: string) => [SUBSCRIPTION_QUERY_KEYS.subscriptions, 'subscription', id],
    billingPeriods: (params?: BillingPeriodsApiOperationsV1BillingPeriodsGetRequest) =>
        params?.subscriptionID
            ? [...SUBSCRIPTION_QUERY_KEYS.subscription(params.subscriptionID), 'billing-periods', { params }]
            : [SUBSCRIPTION_QUERY_KEYS.subscriptions, 'billing-periods', { params }],
    allBillingPeriods: (operationsSubscriptionIDs: string[]) => [
        SUBSCRIPTION_QUERY_KEYS.subscriptions,
        'billing-periods',
        'all',
        { operationsSubscriptionIDs },
    ],
} as const;

export const COMMERCIAL_QUERY_KEYS = {
    billingActivities: (commercialSubscriptionIDs?: string[]) => ['commercial', 'billing-activities', { commercialSubscriptionIDs }],
} as const;

export const AUDIT_LOG_QUERY_KEYS = {
    auditLogs: 'auditLogs',
} as const;

export const CUSTOMER_QUERY_KEYS = {
    customers: 'customers',
    customer: (id: string) => ['customers', 'customer', id],
} as const;

export const SERVICE_LOCATION_QUERY_KEYS = {
    serviceLocations: 'serviceLocations',
    byCustomer: (customerID: string) => ['serviceLocations', 'by-customer', customerID],
} as const;

export const CUSTOMER_DISCOUNT_QUERY_KEYS = {
    customerDiscounts: (customerID: string) => ['customerDiscounts', 'customer', customerID] as const,
} as const;

export const CONTACT_QUERY_KEYS = {
    contacts: 'contacts',
    contact: (customerID: string) => ['contacts', 'customer', customerID],
} as const;

export const RECEIVABLE_QUERY_KEYS = {
    receivables: (params?: Invoice.GetReceivablesQuery) => ['receivables', ...(params ? [{ params }] : [])],
} as const;

export const RECEIVABLE_V2_QUERY_KEYS = {
    receivables: (params?: InvoiceV2.GetReceivablesQuery) => ['receivables-v2', ...(params ? [{ params }] : [])],
    receivable: (id: string) => ['receivables-v2', 'receivable', id],
} as const;

export const TASK_GROUP_QUERY_KEYS = {
    taskGroups: (params?: UseTaskGroupsParams) => ['taskGroups', ...(params ? [{ params }] : [])],
    taskGroup: (id: string) => ['taskGroups', 'taskGroup', id],
    taskGroupLabels: ['taskGroups', 'labels'] as const,
} as const;

/** Params slice used in React Query keys for GET /operations/v1/tasks (excludes hook-only `enabled`). */
export type OperationsTasksListQueryParams = TasksApiOperationsV1TasksGetRequest & { type?: string };

/** Operations-service GET /operations/v1/tasks (roll-off ancillary data, portable-toilet parity) */
export const OPERATIONS_TASKS_QUERY_KEYS = {
    tasks: (params?: OperationsTasksListQueryParams) => ['opsTasks', ...(params ? [{ params }] : [])],
} as const;

export const PROFILE_QUERY_KEYS = {
    waysteHaulerDrivers: ['profile', 'wayste', 'haulerDrivers'] as const,
} as const;

export const HAULER_TERMS_OF_SERVICE_QUERY_KEYS = {
    termsOfService: ['haulerTermsOfService'] as const,
} as const;

export const EVENT_QUERY_KEYS = {
    all: ['events'] as const,
    events: (params?: {
        subscriptionID?: string;
        taskRefIDs?: readonly string[];
        eventStatuses?: readonly string[];
        eventTypes?: readonly string[];
        taskRefTypes?: readonly string[];
        taskRefValues?: readonly string[];
        taskGroupLookupKey?: readonly string[];
        taskRefLookupKey?: readonly string[];
    }) => [...EVENT_QUERY_KEYS.all, ...(params ? [{ params }] : [])],
} as const;

export const INTERNAL_TICKET_QUERY_KEYS = {
    waysteInternalTickets: (params?: InternalTicket.InternalTicketQuery) => ['internalTickets', ...(params ? [{ params }] : [])],
    waysteInternalTicketTags: ['internalTicketTags'] as const,
} as const;

export const PAYMENT_PROFILE_QUERY_KEYS = {
    paymentProfile: ['payment-profile'] as const,
} as const;

export const PAYOUT_QUERY_KEYS = {
    payouts: (params?: Record<string, unknown>) => (params ? ['payouts', { params }] : ['payouts']),
    payout: (id: string | null) => ['payouts', 'payout', id],
    summary: ['payouts', 'summary'] as const,
} as const;

export const RFP_SITE_QUERY_KEYS = {
    rfpSitesByVendor: (vendorID?: string) => (vendorID ? ['rfpSites', 'vendor', vendorID] : ['rfpSites']),
    rfpSiteServicesWithDetails: (rfpSiteId: string) => ['rfpSites', 'site', rfpSiteId, 'servicesWithDetails'] as const,
} as const;

export const RAINFOREST_PAY_QUERY_KEYS = {
    sessions: 'sessions',
    payins: 'payins',
    processingFees: 'processing-fees',
    merchant: 'merchant',
    session: (permissions: string[], allowPendingOnboarding: boolean = false) => [
        RAINFOREST_PAY_QUERY_KEYS.sessions,
        'permissions',
        permissions.toSorted(),
        allowPendingOnboarding,
    ],
    payin: (payinID: string) => [RAINFOREST_PAY_QUERY_KEYS.payins, payinID],
};

export const CUSTOMER_SEARCH_QUERY_KEYS = {
    recent: ['customers', 'recent'],
} as const;

export const STRIPE_CUSTOMER_QUERY_KEYS = {
    stripeCustomer: (stripeID: string) => ['stripe-customer', stripeID],
} as const;

export const DISPOSAL_SITE_QUERY_KEYS = {
    disposalSites: 'disposalSites',
} as const;

export const ORDER_QUERY_KEYS = {
    order: (id: string) => ['order', id] as const,
    /** Bulk order lookup by a set of ord IDs. Sorted+joined so key is stable regardless of input order. */
    ordersByIDs: (ids: readonly string[]) => ['order', 'byIDs', ids.toSorted().join(',')] as const,
} as const;

export const ORDER_RO_QUERY_KEYS = {
    /** Root key — invalidate this to bust every order-RO query (single, list, and search). */
    all: ['orderRo'] as const,
    orderRo: (id: string) => ['orderRo', id] as const,
    /** RO list lookup. Supports by-IDs bulk fetch and paginated browse via params. */
    list: (params: { ids?: readonly string[]; offset?: number; limit?: number }) =>
        [
            'orderRo',
            'list',
            { ids: params.ids ? [...params.ids].toSorted().join(',') : undefined, offset: params.offset, limit: params.limit },
        ] as const,
    /** Paginated ES search for the roll-off order list view. */
    search: (searchText: string, filters: unknown, page: number, pageSize: number, statusChips: readonly string[]) =>
        ['orderRo', 'search', searchText, filters, page, pageSize, statusChips] as const,
    /** Per-status totals for the list view's quick-filter chips. Deliberately excludes any
     *  status selection so every chip keeps its count while one is active. */
    statusSummary: (searchText: string, filters: unknown) => ['orderRo', 'statusSummary', searchText, filters] as const,
} as const;

/** Document images via operations-service docs API — supports multiple ref types/IDs */
export const DOCUMENT_IMAGES_QUERY_KEYS = {
    all: ['documentImages'] as const,
    documentImages: (refs: Array<{ refId: string; refType: string }>) =>
        [...DOCUMENT_IMAGES_QUERY_KEYS.all, ...refs.map((r) => `${r.refType}:${r.refId}`).toSorted()] as const,
} as const;

/** AI extraction for an operations-service dump-ticket document */
export const DUMP_TICKET_READER_QUERY_KEYS = {
    document: (documentId: string) => ['dumpTicketReader', documentId] as const,
} as const;

/** Notes on documents stored as internal tickets with entityType 'wayste-doc' */
export const DOCUMENT_NOTES_QUERY_KEYS = {
    documentNotes: (docId: string) => ['documentNotes', docId] as const,
} as const;

export const CONTAINER_QUERY_KEYS = {
    containers: 'containers',
    containerList: (params?: object) => ['containers', ...(params ? [{ params }] : [])] as const,
    containerType: (id?: string) => ['containers', 'containerType', id] as const,
};
export const TAGGED_CONTAINER_QUERY_KEYS = {
    taggedContainers: ['taggedContainers'] as const,
} as const;

export const LIFTLINE_QUERY_KEYS = {
    seller: (haulerID: string) => ['liftlineSeller', haulerID] as const,
} as const;

export const PRICING_QUERY_KEYS = {
    pricingZone: (id: string) => ['pricingZone', id] as const,
    pricingTiers: (zonePricingID: string) => ['pricingTiers', zonePricingID] as const,
    salesTax: (serviceLocation?: ServiceLocationTransport) => ['salesTax', serviceLocation] as const,
} as const;
